/**
 * Elapsed/behind-schedule durations, written the way a dispatcher would say them.
 *
 * Everything here takes a whole number of minutes and hands back something a
 * person can read at a glance. The reason it exists: the live board renders
 * minute counts that come out of the tracking pipeline unbounded — a visit left
 * DISPATCHED over a weekend produced "23456m late", which is technically the
 * arithmetic and useless to the dispatcher reading it. Past an hour nobody
 * counts in minutes, and past a day the minutes are noise, so the unit grows
 * with the number.
 *
 * Not the same job as `formatDurationFromMinutes` in ./duration — that renders
 * a duration someone TYPED into a labor field and must round-trip exactly, so
 * it never rolls up into days.
 */

const MIN_PER_HOUR = 60;
const MIN_PER_DAY = 24 * MIN_PER_HOUR;

/**
 * "45m", "2h 15m", "3d 4h" — the largest two units that carry information.
 * Negative input is read as its magnitude; callers own the sign/wording.
 */
export function formatElapsedMinutes(minutes: number): string {
  const total = Math.abs(Math.round(minutes));
  if (total < MIN_PER_HOUR) return `${total}m`;

  if (total < MIN_PER_DAY) {
    const hours = Math.floor(total / MIN_PER_HOUR);
    const mins = total % MIN_PER_HOUR;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  }

  const days = Math.floor(total / MIN_PER_DAY);
  const hours = Math.floor((total % MIN_PER_DAY) / MIN_PER_HOUR);
  return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
}

/**
 * How far behind schedule, as a badge reads: "12m late", "2h 15m late".
 *
 * Past a full day the number stops describing a technician who is running
 * behind and starts describing a visit nobody ever closed out, so it says so
 * instead of quoting a figure the dispatcher would have to decode.
 */
export function formatLateness(minutes: number): string {
  const total = Math.round(minutes);
  if (total >= MIN_PER_DAY) return `Overdue ${formatElapsedMinutes(total)}`;
  return `${formatElapsedMinutes(total)} late`;
}

/** Schedule variance, signed: "+35m", "-1h 10m", "0m". */
export function formatVarianceMinutes(minutes: number): string {
  const rounded = Math.round(minutes);
  if (rounded === 0) return '0m';
  return `${rounded > 0 ? '+' : '-'}${formatElapsedMinutes(rounded)}`;
}

/** "just now" / "12m ago" / "3h ago" / "2d ago" for an instant in the past. */
export function formatMinutesAgo(minutes: number): string {
  if (minutes <= 0) return 'just now';
  return `${formatElapsedMinutes(minutes)} ago`;
}
